import {
  ApiConversion,
  ApiConversionType,
  ApiProjectDefault,
  ApiProjectSettingKey,
  ApiReservedConversion,
} from "@incendium/api";
import {
  Box,
  TableRow,
  TableBody,
  styled,
  TableCell,
  TableHead,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { syncService } from "Apis";
import Loading from "Components/Loading/Loading";
import SpacedList from "Components/UI/SpacedList";
import { sortAlpha } from "Helpers/arrays";
import { friendlyDate, friendlyDateTime } from "Helpers/dates";
import { useNotification, useSelectedProject } from "Hooks";
import { useConversions } from "Hooks/useConversions";
import { saveConversion } from "features/location";
import { saveProjectSetting } from "features/project";
import PushedMetaEventsChart from "features/signals/components/PushedMetaEventsChart";
import SignalStatusCard from "features/signals/components/SignalStatusCard";
import { TChartData } from "Interfaces";
import produce from "immer";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";

// Maps reserved conversions to Meta Pixel standard event names.
// matches the standardEventsMap in pkg/sync/push_facebook_events.go
const standardEventsMap: Partial<Record<ApiReservedConversion, string>> = {
  [ApiReservedConversion.ADD_TO_BASKET]: "AddToCart",
  [ApiReservedConversion.CHECKOUT]: "InitiateCheckout",
  [ApiReservedConversion.CART_VIEWED]: "ViewContent",
  [ApiReservedConversion.SALE]: "Purchase",
  [ApiReservedConversion.ENQUIRY_SUBMITTED]: "Lead",
  [ApiReservedConversion.SEARCH]: "Search",
  [ApiReservedConversion.PAYMENT_INFO_SUBMITTED]: "AddPaymentInfo",
  [ApiReservedConversion.WISHLIST_ADDED]: "AddToWishlist",
  [ApiReservedConversion.JOB_APPLICATION]: "SubmitApplication",
  [ApiReservedConversion.COMPLETE_REGISTRATION]: "CompleteRegistration",
  [ApiReservedConversion.START_TRIAL]: "StartTrial",
};

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "grid",
  gridTemplateColumns: "6fr 3fr",
  alignItems: "stretch",
  gridTemplateRows: "370px auto",
  gap: theme.spacing(4),
}));

function MetaSignalsPage() {
  const { conversions, fetched, setConversions } = useConversions();
  const { selectedProject, projectSettings, setProjectSettings } =
    useSelectedProject();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [pushedMetaResults, setPushedMetaResults] = useState<
    Array<{ date?: Date; pushedCount?: number; blockedCount?: number }>
  >([]);
  const [pushedMetaLoading, setPushedMetaLoading] = useState(true);

  useEffect(() => {
    if (!selectedProject?.id) {
      setPushedMetaResults([]);
      setPushedMetaLoading(false);
      return;
    }
    setPushedMetaLoading(true);
    syncService
      .syncServiceGetPushedMetaEvents({ projectId: selectedProject.id })
      .then((res) => setPushedMetaResults(res.results ?? []))
      .finally(() => setPushedMetaLoading(false));
  }, [selectedProject?.id]);

  const pushedMetaChartData = useMemo<TChartData[]>(() => {
    const byDay: Record<string, { pushed: number; blocked: number }> = {};
    for (const row of pushedMetaResults) {
      if (!row.date) continue;
      const key = moment(row.date).format("YYYY-MM-DD");
      if (!byDay[key]) byDay[key] = { pushed: 0, blocked: 0 };
      byDay[key].pushed += row.pushedCount ?? 0;
      byDay[key].blocked += row.blockedCount ?? 0;
    }
    return Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateStr, counts]) => ({
        name: friendlyDate(dateStr),
        pushed: counts.pushed,
        blocked: counts.blocked,
      }));
  }, [pushedMetaResults]);

  const signalledConversions = useMemo(() => {
    const sales = conversions.filter(
      (c) =>
        c.reserved === ApiReservedConversion.SALE ||
        c._default === ApiProjectDefault.ECOMMERCE,
    );
    const other = conversions.filter(
      (c) =>
        c.reserved !== ApiReservedConversion.SALE &&
        c._default !== ApiProjectDefault.ECOMMERCE,
    );
    return [...sales, ...sortAlpha(other, "name")];
  }, [conversions]);

  const handlePushToMetaChange = async (conversion: ApiConversion) => {
    try {
      // location id no longer needed
      const res = await saveConversion(
        selectedProject?.clientId as number,
        selectedProject?.id as number,
        0,
        {
          ...conversion,
          pushToMeta: !conversion.pushToMeta,
        },
      );

      setConversions(
        produce(conversions, (draft) => {
          const idx = draft.findIndex((d) => d.id === conversion.id);
          if (idx >= 0) {
            draft[idx] = res.result as ApiConversion;
          }
        }),
      );

      showSuccessNotification(
        `${conversion.pushToMeta ? "Trigger Off" : "Trigger On"} for ${conversion.name}`,
      );
    } catch (error) {
      showErrorNotification(
        `Failed to ${conversion.pushToMeta ? "Trigger Off" : "Trigger On"} for ${conversion.name}`,
      );
    }
  };

  const handleEffectiveSessionToMetaChange = async () => {
    if (!selectedProject) return;
    const key = ApiProjectSettingKey.PUSH_EFFECTIVE_SESSIONS_TO_META;
    const next = projectSettings[key] === "true" ? "false" : "true";
    try {
      await saveProjectSetting({ key, value: next }, selectedProject);
      setProjectSettings((s) => ({ ...s, [key]: next }));
      showSuccessNotification(
        next === "true"
          ? "Effective Session push to Meta on"
          : "Effective Session push to Meta off",
      );
    } catch {
      showErrorNotification("Failed to update Effective Session setting");
    }
  };

  const handleEffectivePageviewToMetaChange = async () => {
    if (!selectedProject) return;
    const key = ApiProjectSettingKey.PUSH_EFFECTIVE_PAGE_VIEWS_TO_META;
    const next = projectSettings[key] === "true" ? "false" : "true";
    try {
      await saveProjectSetting({ key, value: next }, selectedProject);
      setProjectSettings((s) => ({ ...s, [key]: next }));
      showSuccessNotification(
        next === "true"
          ? "Effective Pageview push to Meta on"
          : "Effective Pageview push to Meta off",
      );
    } catch {
      showErrorNotification("Failed to update Effective Pageview setting");
    }
  };

  if (!fetched || !selectedProject) {
    return <Loading />;
  }

  return (
    <StyledContainer>
      <Box>
        <PushedMetaEventsChart
          chartData={pushedMetaChartData}
          loading={pushedMetaLoading}
        />
      </Box>
      <Box>
        <SignalStatusCard
          chartData={pushedMetaChartData}
          loading={pushedMetaLoading}
        />
      </Box>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <SpacedList title="Event Signals List">
          <TableHead>
            <TableRow>
              <TableCell>Event Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Event Last Fired</TableCell>
              <TableCell>Meta CAPI</TableCell>
              <TableCell>Match Quality Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Effective Session</TableCell>
              <TableCell>Session</TableCell>
              <TableCell>N/A</TableCell>
              <TableCell>
                <FormControlLabel
                  sx={{ minWidth: 136, marginRight: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={
                        projectSettings[
                          ApiProjectSettingKey.PUSH_EFFECTIVE_SESSIONS_TO_META
                        ] === "true"
                      }
                      onChange={handleEffectiveSessionToMetaChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {projectSettings[
                        ApiProjectSettingKey.PUSH_EFFECTIVE_SESSIONS_TO_META
                      ] === "true"
                        ? "Trigger On"
                        : "Trigger Off"}
                    </Typography>
                  }
                />
              </TableCell>
              <TableCell>N/A</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Effective Pageview</TableCell>
              <TableCell>Pageview</TableCell>
              <TableCell>N/A</TableCell>
              <TableCell>
                <FormControlLabel
                  sx={{ minWidth: 136, marginRight: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={
                        projectSettings[
                          ApiProjectSettingKey.PUSH_EFFECTIVE_PAGE_VIEWS_TO_META
                        ] === "true"
                      }
                      onChange={handleEffectivePageviewToMetaChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {projectSettings[
                        ApiProjectSettingKey.PUSH_EFFECTIVE_PAGE_VIEWS_TO_META
                      ] === "true"
                        ? "Trigger On"
                        : "Trigger Off"}
                    </Typography>
                  }
                />
              </TableCell>
              <TableCell>N/A</TableCell>
            </TableRow>
            {signalledConversions.map((conversion) => (
              <TableRow key={conversion.id}>
                <TableCell>
                  {conversion.reserved && standardEventsMap[conversion.reserved]
                    ? `${standardEventsMap[conversion.reserved]} (${conversion.name})`
                    : conversion.name}
                </TableCell>
                <TableCell>
                  {conversion.reserved === ApiReservedConversion.SALE ||
                  conversion._default === ApiProjectDefault.ECOMMERCE
                    ? "Sale"
                    : conversion.type === ApiConversionType.MACRO
                      ? "Conversion"
                      : "Interaction"}
                </TableCell>
                <TableCell>
                  {conversion.lastFiredAt
                    ? friendlyDateTime(conversion.lastFiredAt)
                    : "Never"}
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    sx={{ minWidth: 136, marginRight: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={conversion.pushToMeta}
                        onChange={() => handlePushToMetaChange(conversion)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {conversion.pushToMeta ? "Trigger On" : "Trigger Off"}
                      </Typography>
                    }
                  />
                </TableCell>
                <TableCell>
                  {conversion.pushToMeta
                    ? conversion.metaMatchQuality
                      ? `${conversion.metaMatchQuality}`
                      : "No Score"
                    : "N/A"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </SpacedList>
      </Box>
    </StyledContainer>
  );
}

export default MetaSignalsPage;
